import React, { useState, useEffect } from "react";
import { ShowButton } from "@refinedev/antd";
import { useList, useCustomMutation } from "@refinedev/core";
import { Button, Space, Table, Tag, Tooltip, Modal, Form, Select, Input, message, Card } from "antd";
import { PrinterOutlined, SyncOutlined, PlusOutlined, RollbackOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";
import { invoiceRegenerationService, RegenerationRequest } from "../../api/invoiceRegenerationService";

const { TextArea } = Input;

interface SaleReturn {
    id: number;
    saleId: number;
    status: string;
    returnReason: string;
    createdAt: string;
    documentUrl?: string;
    sale: {
        id: number;
        invoiceNo: string;
        customerName: string;
        status: string;
        currencyTypeCode: string;
        totalAmount: number;
    };
}

export const SaleReturnList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<SaleReturn[]>([]);
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [selectedSaleReturn, setSelectedSaleReturn] = useState<SaleReturn | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const { data, isLoading, refetch } = useList({
        resource: "salereturns",
    });

    const { mutate: approve } = useCustomMutation();
    const { mutate: reject } = useCustomMutation();

    useEffect(() => {
        if (!data?.data) return;

        let returns = data.data as SaleReturn[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            returns = returns.filter((r: SaleReturn) =>
                r.sale?.invoiceNo?.toLowerCase().includes(searchLower) ||
                r.sale?.customerName?.toLowerCase().includes(searchLower) ||
                r.status?.toLowerCase().includes(searchLower) ||
                r.returnReason?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...returns].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const handleApprove = (id: number) => {
        approve({ url: `salereturns/${id}/approve`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handleReject = (id: number) => {
        reject({ url: `salereturns/${id}/reject`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handlePrint = (record: SaleReturn) => {
        if (record?.documentUrl) {
            window.open(record.documentUrl, '_blank');
        }
    };

    const handleRegenerateSubmit = async (values: any) => {
        if (!selectedSaleReturn) return;
        setRegenerating(true);
        try {
            const request: RegenerationRequest = {
                regenerationType: values.regenerationType,
                reason: values.reason,
                preserveOriginal: true,
                notifyCustomer: false,
            };
            const result = await invoiceRegenerationService.regenerateSaleReturn(selectedSaleReturn.id, request);
            if (result.success) {
                message.success(`Sale return regenerated!`);
                setRegenerateModalVisible(false);
                refetch();
            } else {
                message.error(`Failed: ${result.errorMessage}`);
            }
        } catch (error: any) {
            message.error(`Failed: ${error.message}`);
        } finally {
            setRegenerating(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Approved": return "processing";
            case "Draft": return "default";
            case "Completed": return "success";
            case "Rejected": return "error";
            default: return "default";
        }
    };

    const columns = [
        {
            title: "Return ID",
            dataIndex: "id",
            key: "id",
            render: (value: number) => (
                <span className="font-medium text-text-primary">#{value}</span>
            ),
        },
        {
            title: "Sale #",
            key: "saleInvoiceNo",
            render: (_: any, record: SaleReturn) => (
                <span className="text-text-secondary">{record.sale?.invoiceNo || "-"}</span>
            ),
        },
        {
            title: "Customer",
            key: "customerName",
            render: (_: any, record: SaleReturn) => (
                <span className="text-text-secondary">{record.sale?.customerName || "-"}</span>
            ),
        },
        {
            title: "Reason",
            dataIndex: "returnReason",
            key: "returnReason",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">{value || "-"}</span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: string) => (
                <Tag color={getStatusColor(value)} className="status-tag">{value}</Tag>
            ),
        },
        {
            title: "Amount",
            key: "totalAmount",
            render: (_: any, record: SaleReturn) => (
                <span className="font-semibold text-primary">
                    {record.sale?.currencyTypeCode || '$'} {record.sale?.totalAmount?.toFixed(2) || '0.00'}
                </span>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 200,
            render: (_: any, record: SaleReturn) => (
                <Space size="small" wrap>
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    {record?.documentUrl && (
                        <>
                            <Tooltip title="Print">
                                <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
                            </Tooltip>
                            <Tooltip title="Regenerate">
                                <Button size="small" icon={<SyncOutlined />} onClick={() => { setSelectedSaleReturn(record); setRegenerateModalVisible(true); }} />
                            </Tooltip>
                        </>
                    )}
                    {record.status === "Draft" && (
                        <>
                            <Button size="small" type="primary" onClick={() => handleApprove(record.id)}>Approve</Button>
                            <Button size="small" danger onClick={() => handleReject(record.id)}>Reject</Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Sale Returns"
                icon={<RollbackOutlined />}
                breadcrumbs={[{ title: "Sale Returns" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/salereturns/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by sale number, customer, or status"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 1000 }}
                    />
                </Card>
            </div>

            <Modal
                title={`Regenerate Sale Return ${selectedSaleReturn?.sale?.invoiceNo || ""}`}
                open={regenerateModalVisible}
                onCancel={() => { setRegenerateModalVisible(false); setSelectedSaleReturn(null); }}
                footer={null}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleRegenerateSubmit} initialValues={{ regenerationType: "Manual" }}>
                    <Form.Item name="regenerationType" label="Regeneration Type" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="Manual">Manual</Select.Option>
                            <Select.Option value="BugFix">Bug Fix</Select.Option>
                            <Select.Option value="LayoutUpdate">Layout Update</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
                        <TextArea rows={3} placeholder="Reason for regeneration" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={regenerating}>Regenerate</Button>
                            <Button onClick={() => setRegenerateModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
